import React, { useEffect, useMemo, useState } from "react";

// const API_BASE = "http://localhost:9090/api/teachers";
const API_BASE = "https://node-api.mediitacademy.com/api/teachers";

type TeacherStatus = "Active" | "On Leave" | "Inactive" | "Pending";

type Teacher = {
  id: number;
  adminUserId?: number | null;
  name: string;
  email: string;
  phone: string;
  subject: string;
  experience: string;
  bio?: string;
  batches: number;
  students: number;
  rating: number;
  status: TeacherStatus;
  joiningDate?: string | null;
};

type Stats = {
  totalTeachers: number;
  activeTeachers: number;
  assignedBatches: number;
  totalStudents: number;
  avgRating: number;
};

const emptyStats: Stats = {
  totalTeachers: 0,
  activeTeachers: 0,
  assignedBatches: 0,
  totalStudents: 0,
  avgRating: 0,
};

const defaultSubjects = ["All", "Biology", "Physics", "Chemistry", "Mathematics", "General"];

const getAuthHeaders = (): Record<string, string> => {
  const auth = localStorage.getItem("mediit_admin_auth");
  const parsedAuth = auth ? safeJsonParse(auth) : null;
  const token = parsedAuth?.token || localStorage.getItem("adminToken") || localStorage.getItem("token");

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) headers.Authorization = `Bearer ${token}`;

  return headers;
};

const safeJsonParse = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const AdminTeachers: React.FC = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [stats, setStats] = useState<Stats>(emptyStats);
  const [activeSubject, setActiveSubject] = useState("All");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "Biology",
    experience: "",
    bio: "",
    status: "Active" as TeacherStatus,
    rating: "",
  });

  const fetchTeachers = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(API_BASE, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || data?.message || "Failed to load teachers.");
      }

      const incomingTeachers: Teacher[] = Array.isArray(data) ? data : data?.teachers || [];
      const incomingStats: Stats = data?.stats || emptyStats;

      setTeachers(
        incomingTeachers.map((teacher) => ({
          ...teacher,
          batches: Number(teacher.batches || 0),
          students: Number(teacher.students || 0),
          rating: Number(teacher.rating || 0),
          status: (teacher.status || "Active") as TeacherStatus,
          experience: teacher.experience || "",
          subject: teacher.subject || "General",
        }))
      );

      setStats({ ...emptyStats, ...incomingStats });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setTeachers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  const subjects = useMemo(() => {
    const set = new Set<string>(defaultSubjects);
    teachers.forEach((teacher) => {
      if (teacher.subject) set.add(teacher.subject);
    });
    return Array.from(set);
  }, [teachers]);

  const filteredTeachers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return teachers.filter((teacher) => {
      const matchSubject = activeSubject === "All" || teacher.subject === activeSubject;

      const searchableText = `${teacher.name} ${teacher.email} ${teacher.phone} ${teacher.subject} ${teacher.experience} ${teacher.status}`.toLowerCase();

      const matchSearch = !normalizedSearch || searchableText.includes(normalizedSearch);

      return matchSubject && matchSearch;
    });
  }, [teachers, activeSubject, search]);

  const derivedStats: Stats = useMemo(() => {
    return {
      totalTeachers: stats.totalTeachers || teachers.length,
      activeTeachers: stats.activeTeachers || teachers.filter((t) => t.status === "Active").length,
      assignedBatches: stats.assignedBatches || teachers.reduce((total, t) => total + t.batches, 0),
      totalStudents: stats.totalStudents || teachers.reduce((total, t) => total + t.students, 0),
      avgRating:
        stats.avgRating ||
        Number(
          (
            teachers.filter((t) => t.rating > 0).reduce((total, t) => total + t.rating, 0) /
            Math.max(teachers.filter((t) => t.rating > 0).length, 1)
          ).toFixed(1)
        ),
    };
  }, [teachers, stats]);

  const resetForm = () => {
    setEditingTeacher(null);
    setForm({
      name: "",
      email: "",
      phone: "",
      subject: "Biology",
      experience: "",
      bio: "",
      status: "Active",
      rating: "",
    });
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (teacher: Teacher) => {
    setEditingTeacher(teacher);
    setForm({
      name: teacher.name || "",
      email: teacher.email || "",
      phone: teacher.phone || "",
      subject: teacher.subject || "General",
      experience: teacher.experience || "",
      bio: teacher.bio || "",
      status: teacher.status || "Active",
      rating: teacher.rating ? String(teacher.rating) : "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const handleSaveTeacher = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      alert("Teacher name, email aur phone required hai.");
      return;
    }

    if (!form.email.includes("@")) {
      alert("Valid email enter karo.");
      return;
    }

    if (!/^\d{10}$/.test(form.phone.replace(/\D/g, ""))) {
      alert("Phone number exactly 10 digits hona chahiye.");
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(editingTeacher ? `${API_BASE}/${editingTeacher.id}` : API_BASE, {
        method: editingTeacher ? "PATCH" : "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.replace(/\D/g, "").slice(0, 10),
          subject: form.subject,
          experience: form.experience.trim(),
          bio: form.bio.trim(),
          status: form.status,
          rating: form.rating ? Number(form.rating) : 0,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || data?.message || "Teacher save failed.");
      }

      closeModal();
      await fetchTeachers();
      alert(editingTeacher ? "Teacher updated successfully!" : "Teacher profile created successfully!");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Teacher save failed.");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (teacher: Teacher, status: TeacherStatus) => {
    const key = `status-${teacher.id}`;
    setSavingId(key);

    try {
      const response = await fetch(`${API_BASE}/${teacher.id}/status`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ status }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || data?.message || "Status update failed.");
      }

      setTeachers((prev) => prev.map((item) => (item.id === teacher.id ? { ...item, status } : item)));
    } catch (err) {
      alert(err instanceof Error ? err.message : "Status update failed.");
    } finally {
      setSavingId(null);
    }
  };

  const deactivateTeacher = async (teacher: Teacher) => {
    const confirm = window.confirm(`${teacher.name} ko inactive karna hai?`);
    if (!confirm) return;

    const key = `delete-${teacher.id}`;
    setSavingId(key);

    try {
      const response = await fetch(`${API_BASE}/${teacher.id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || data?.message || "Teacher deactivate failed.");
      }

      setTeachers((prev) => prev.map((item) => (item.id === teacher.id ? { ...item, status: "Inactive" } : item)));
    } catch (err) {
      alert(err instanceof Error ? err.message : "Teacher deactivate failed.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <section className="relative overflow-hidden rounded-[30px] bg-slate-950 p-6 text-white shadow-xl shadow-slate-300/40 sm:p-8">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="absolute -bottom-24 left-1/2 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />

        <div className="relative flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
          <div>
            <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
              Dynamic Teacher Management
            </div>

            <h1 className="max-w-3xl text-3xl font-black tracking-tight sm:text-4xl">
              Manage teachers, subjects and assigned batches
            </h1>

            <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
              Teacher registration Spring Boot se rahega. Listing, search, status, profile update aur batch count Node API se fast load hoga.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={fetchTeachers}
              className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15"
            >
              Refresh
            </button>

            <button
              onClick={openCreateModal}
              className="rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50"
            >
              + Add Teacher Profile
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="grid gap-5 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard title="Total Teachers" value={derivedStats.totalTeachers.toString()} note="All teacher profiles" />
        <StatCard title="Active Teachers" value={derivedStats.activeTeachers.toString()} note="Currently teaching" />
        <StatCard title="Assigned Batches" value={derivedStats.assignedBatches.toString()} note="Across all courses" />
        <StatCard title="Students Linked" value={derivedStats.totalStudents.toLocaleString("en-IN")} note="From enrolled batches" />
        <StatCard title="Avg Rating" value={derivedStats.avgRating ? `${derivedStats.avgRating}/5` : "New"} note="Student feedback" />
      </section>

      {/* Filters */}
      <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap gap-2">
            {subjects.map((subject) => (
              <button
                key={subject}
                onClick={() => setActiveSubject(subject)}
                className={`rounded-2xl px-4 py-2 text-sm font-black transition ${
                  activeSubject === subject
                    ? "bg-slate-950 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {subject}
              </button>
            ))}
          </div>

          <div className="flex h-12 w-full items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 xl:max-w-md">
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" />
            </svg>

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search teacher, email, phone, subject..."
              className="w-full bg-transparent text-sm font-semibold outline-none placeholder:text-slate-400"
            />
          </div>
        </div>
      </section>

      {error && (
        <section className="rounded-[24px] border border-red-100 bg-red-50 p-5 text-sm font-black text-red-700">
          {error}
        </section>
      )}

      {/* Main Grid */}
      <section className="grid gap-6 xl:grid-cols-[1fr_360px]">
        {/* Teachers */}
        <div className="grid gap-5 lg:grid-cols-2">
          {loading ? (
            <div className="col-span-full rounded-[28px] border border-slate-200 bg-white p-8 text-center text-sm font-black text-slate-500">
              Loading teachers...
            </div>
          ) : filteredTeachers.length === 0 ? (
            <div className="col-span-full rounded-[28px] border border-slate-200 bg-white p-8 text-center">
              <h2 className="text-xl font-black text-slate-950">No teachers found</h2>
              <p className="mt-2 text-sm font-semibold text-slate-500">
                Spring Boot admin signup me role Teacher select karke register karo, ya yahan profile manually add karo.
              </p>
              <button
                onClick={openCreateModal}
                className="mt-5 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
              >
                + Add Teacher Profile
              </button>
            </div>
          ) : (
            filteredTeachers.map((teacher) => (
              <div
                key={teacher.id}
                className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-4">
                    <Avatar name={teacher.name} />

                    <div>
                      <div className="flex flex-wrap gap-2">
                        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
                          {teacher.subject}
                        </span>
                        <StatusBadge status={teacher.status} />
                      </div>

                      <h2 className="mt-3 text-lg font-black leading-6 text-slate-950">
                        {teacher.name}
                      </h2>

                      <p className="mt-1 break-all text-sm font-semibold text-slate-500">
                        {teacher.email}
                      </p>
                      <p className="text-sm font-semibold text-slate-400">
                        {teacher.phone}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => openEditModal(teacher)}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-black text-slate-600 hover:bg-slate-50"
                  >
                    Edit
                  </button>
                </div>

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <InfoBox label="Experience" value={teacher.experience || "Not added"} />
                  <InfoBox label="Batches" value={teacher.batches.toString()} />
                  <InfoBox label="Students" value={teacher.students.toLocaleString("en-IN")} />
                  <InfoBox label="Rating" value={teacher.rating ? `${teacher.rating}/5` : "New"} />
                </div>

                <div className="mt-5 rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-bold text-slate-400">Joining Date</p>
                  <p className="mt-1 text-sm font-black text-slate-900">
                    {formatDate(teacher.joiningDate)}
                  </p>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <select
                    value={teacher.status}
                    disabled={savingId === `status-${teacher.id}`}
                    onChange={(e) => updateStatus(teacher, e.target.value as TeacherStatus)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-black text-slate-700 outline-none hover:bg-slate-100"
                  >
                    <option value="Active">Active</option>
                    <option value="On Leave">On Leave</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Pending">Pending</option>
                  </select>

                  <button
                    onClick={() => deactivateTeacher(teacher)}
                    disabled={savingId === `delete-${teacher.id}` || teacher.status === "Inactive"}
                    className="rounded-2xl bg-red-50 px-4 py-2.5 text-xs font-black text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {savingId === `delete-${teacher.id}` ? "Saving..." : "Deactivate"}
                  </button>
                </div>

                <div className="mt-3 flex flex-wrap gap-3">
                  <button className="rounded-2xl bg-slate-950 px-4 py-2.5 text-xs font-black text-white hover:bg-blue-600">
                    View Profile
                  </button>
                  <button className="rounded-2xl bg-slate-100 px-4 py-2.5 text-xs font-black text-slate-700 hover:bg-slate-200">
                    Assign Batch
                  </button>
                  <button className="rounded-2xl bg-slate-100 px-4 py-2.5 text-xs font-black text-slate-700 hover:bg-slate-200">
                    Schedule
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Right Panel */}
        <aside className="space-y-6">
          <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-black text-slate-950">Quick Actions</h2>

            <div className="mt-5 grid gap-3">
              {["Add New Teacher", "Assign Batch", "Create Teacher Login", "Schedule Class", "Send Notice"].map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    if (item === "Add New Teacher") openCreateModal();
                    if (item === "Create Teacher Login") window.location.href = "/admin-auth";
                  }}
                  className="group flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-left text-sm font-black text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                >
                  {item}
                  <span className="transition group-hover:translate-x-1">→</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-black text-slate-950">Top Teachers</h2>

            <div className="mt-5 space-y-4">
              {teachers
                .filter((item) => item.rating > 0)
                .sort((a, b) => b.rating - a.rating)
                .slice(0, 3)
                .map((teacher, index) => (
                  <div key={teacher.id} className="flex items-center gap-3 rounded-2xl bg-slate-50 p-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-sm font-black text-blue-700">
                      #{index + 1}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-black text-slate-950">{teacher.name}</p>
                      <p className="truncate text-xs font-bold text-slate-500">{teacher.subject}</p>
                    </div>

                    <p className="text-sm font-black text-emerald-600">{teacher.rating}</p>
                  </div>
                ))}

              {teachers.filter((item) => item.rating > 0).length === 0 && (
                <p className="rounded-2xl bg-slate-50 p-4 text-sm font-bold text-slate-500">
                  Rating abhi add nahi hui.
                </p>
              )}
            </div>
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-black text-slate-950">Workload Overview</h2>

            <div className="mt-5 space-y-4">
              {subjects
                .filter((subject) => subject !== "All")
                .slice(0, 5)
                .map((subject) => {
                  const subjectTeachers = teachers.filter((teacher) => teacher.subject === subject);
                  const totalBatches = subjectTeachers.reduce((total, teacher) => total + teacher.batches, 0);
                  const value = Math.min(100, totalBatches * 12 + subjectTeachers.length * 10);

                  return <Workload key={subject} label={subject} value={`${value}%`} />;
                })}
            </div>
          </div>
        </aside>
      </section>

      {/* Add/Edit Teacher Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-2xl rounded-[30px] bg-white p-5 shadow-2xl sm:p-6">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black text-slate-950">
                  {editingTeacher ? "Edit Teacher" : "Add New Teacher Profile"}
                </h2>
                <p className="mt-1 text-sm font-semibold text-slate-500">
                  Login account Spring Boot signup se create hoga. Ye profile Node teacher table me save hogi.
                </p>
              </div>

              <button
                onClick={closeModal}
                className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-xl font-black text-slate-600 hover:bg-slate-200"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSaveTeacher} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormInput
                  label="Teacher Name"
                  value={form.name}
                  placeholder="Enter teacher name"
                  onChange={(value) => setForm({ ...form, name: value })}
                />

                <FormInput
                  label="Email"
                  type="email"
                  value={form.email}
                  placeholder="Enter email"
                  onChange={(value) => setForm({ ...form, email: value })}
                />

                <FormInput
                  label="Phone"
                  value={form.phone}
                  placeholder="Enter phone number"
                  onChange={(value) => setForm({ ...form, phone: value.replace(/\D/g, "").slice(0, 10) })}
                />

                <FormInput
                  label="Experience"
                  value={form.experience}
                  placeholder="Example: 5 Years"
                  onChange={(value) => setForm({ ...form, experience: value })}
                />

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
                    Subject
                  </label>
                  <select
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
                  >
                    {subjects
                      .filter((subject) => subject !== "All")
                      .map((subject) => (
                        <option key={subject} value={subject}>
                          {subject}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
                    Status
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value as TeacherStatus })}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
                  >
                    <option value="Active">Active</option>
                    <option value="On Leave">On Leave</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>

                <FormInput
                  label="Rating"
                  type="number"
                  value={form.rating}
                  placeholder="Example: 4.8"
                  onChange={(value) => setForm({ ...form, rating: value })}
                />

                <div className="sm:col-span-2">
                  <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
                    Bio / Notes
                  </label>
                  <textarea
                    value={form.bio}
                    placeholder="Teacher details, specialisation, notes..."
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    rows={3}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white"
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 pt-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {saving ? "Saving..." : editingTeacher ? "Update Teacher" : "Create Teacher Profile"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({
  title,
  value,
  note,
}: {
  title: string;
  value: string;
  note: string;
}) => {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-bold text-slate-500">{title}</p>
      <h2 className="mt-2 text-3xl font-black text-slate-950">{value}</h2>
      <p className="mt-1 text-xs font-bold text-emerald-600">{note}</p>
    </div>
  );
};

const Avatar = ({ name }: { name: string }) => {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white shadow-lg">
      {initials || "T"}
    </div>
  );
};

const StatusBadge = ({ status }: { status: TeacherStatus }) => {
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-black ${
        status === "Active"
          ? "bg-emerald-50 text-emerald-700"
          : status === "On Leave"
          ? "bg-orange-50 text-orange-700"
          : status === "Pending"
          ? "bg-blue-50 text-blue-700"
          : "bg-red-50 text-red-700"
      }`}
    >
      {status}
    </span>
  );
};

const InfoBox = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="rounded-2xl bg-slate-50 p-3">
      <p className="text-xs font-bold text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-black text-slate-900">{value}</p>
    </div>
  );
};

const Workload = ({ label, value }: { label: string; value: string }) => {
  return (
    <div>
      <div className="mb-2 flex justify-between text-sm font-black text-slate-700">
        <span>{label}</span>
        <span>{value}</span>
      </div>

      <div className="h-2 rounded-full bg-slate-100">
        <div className="h-2 rounded-full bg-blue-600" style={{ width: value }} />
      </div>
    </div>
  );
};

const FormInput = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  placeholder: string;
  type?: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div>
      <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </label>

      <input
        type={type}
        value={value}
        placeholder={placeholder}
        step={type === "number" ? "0.1" : undefined}
        min={type === "number" ? "0" : undefined}
        max={type === "number" ? "5" : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white"
      />
    </div>
  );
};

export default AdminTeachers;
